import React, { useState, useEffect, useRef } from 'react'
import { Key, Eye, EyeOff, Check, X, AlertCircle } from 'lucide-react'
import SectionHeading from './SectionHeading'

interface SecretMetadata {
  secretKey: string
  exists: boolean
  maskedValue?: string
}

export function ApiKeysSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // State for each secret
  const [claudeApiKey, setClaudeApiKey] = useState('')
  const [claudeKeyExists, setClaudeKeyExists] = useState(false)
  const [showClaudeKey, setShowClaudeKey] = useState(false)
  const [claudeKeyShared, setClaudeKeyShared] = useState(false)

  const [openaiApiKey, setOpenaiApiKey] = useState('')
  const [openaiKeyExists, setOpenaiKeyExists] = useState(false)
  const [showOpenaiKey, setShowOpenaiKey] = useState(false)
  const [openaiKeyShared, setOpenaiKeyShared] = useState(false)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
    }
  }, [])

  // Load current secrets status
  useEffect(() => {
    let isMounted = true

    const loadSecretsStatus = async () => {
      try {
        if (isMounted) {
          setLoading(true)
          setError(null)
        }

        // Get token from localStorage (for VFC login) or rely on cookie (for OAuth)
        const token = localStorage.getItem('auth-token')
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        }

        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const response = await fetch('/api/admin/tenant-secrets', {
          method: 'GET',
          credentials: 'include',
          headers
        })

        if (!response.ok) {
          throw new Error('Failed to load API key status')
        }

        const data = await response.json()

        // Backend returns metadata object with boolean values
        if (isMounted) {
          setClaudeKeyExists(data.metadata?.claudeApiKey || false)
          setOpenaiKeyExists(data.metadata?.openaiApiKey || false)
        }

      } catch (err) {
        console.error('Error loading secrets:', err)
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load API key status')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadSecretsStatus()
    return () => { isMounted = false }
  }, [])

  const loadSecretsStatus = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get token from localStorage (for VFC login) or rely on cookie (for OAuth)
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/admin/tenant-secrets', {
        method: 'GET',
        credentials: 'include',
        headers
      })

      if (!response.ok) {
        throw new Error('Failed to load API key status')
      }

      const data = await response.json()

      // Backend returns metadata object with boolean values
      setClaudeKeyExists(data.metadata?.claudeApiKey || false)
      setOpenaiKeyExists(data.metadata?.openaiApiKey || false)

    } catch (err) {
      console.error('Error loading secrets:', err)
      setError(err instanceof Error ? err.message : 'Failed to load API key status')
    } finally {
      setLoading(false)
    }
  }

  const saveSecret = async (secretKey: string, secretValue: string, shared: boolean) => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Get token from localStorage (for VFC login) or rely on cookie (for OAuth)
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/admin/tenant-secrets', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          secretKey,
          secretValue,
          shared
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to save API key')
      }

      setSuccess('API key saved successfully')

      // Reload status to update UI
      await loadSecretsStatus()

      // Clear input fields
      if (secretKey === 'claudeApiKey') {
        setClaudeApiKey('')
        setShowClaudeKey(false)
      } else if (secretKey === 'openaiApiKey') {
        setOpenaiApiKey('')
        setShowOpenaiKey(false)
      }

      // Clear success message after 3 seconds
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
      successTimeoutRef.current = setTimeout(() => {
        setSuccess(null)
        successTimeoutRef.current = null
      }, 3000)

    } catch (err) {
      console.error('Error saving secret:', err)
      setError(err instanceof Error ? err.message : 'Failed to save API key')
    } finally {
      setSaving(false)
    }
  }

  const deleteSecret = async (secretKey: string) => {
    if (!confirm(`Are you sure you want to delete this API key?`)) {
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Get token from localStorage (for VFC login) or rely on cookie (for OAuth)
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/admin/tenant-secrets', {
        method: 'DELETE',
        credentials: 'include',
        headers,
        body: JSON.stringify({ secretKey })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to delete API key')
      }

      setSuccess('API key deleted successfully')

      // Reload status to update UI
      await loadSecretsStatus()

      // Clear success message after 3 seconds
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
      successTimeoutRef.current = setTimeout(() => {
        setSuccess(null)
        successTimeoutRef.current = null
      }, 3000)

    } catch (err) {
      console.error('Error deleting secret:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete API key')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2 text-gray-600">
          <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span>Loading API keys...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-4">
          <SectionHeading id="api-keys" className="text-lg font-semibold text-gray-900">
            <>
              <Key className="w-6 h-6 text-blue-600 mr-3" />
              API Keys
            </>
          </SectionHeading>
        </div>
        <p className="text-gray-600 mb-4">
          Securely manage API keys for third-party integrations. Keys are encrypted at rest and never exposed in logs or responses.
        </p>

        {/* Alert messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-green-800">{success}</span>
          </div>
        )}
      </div>

      {/* Claude API Key */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-md font-semibold text-gray-900 mb-2">Anthropic Claude API Key</h3>
        <p className="text-sm text-gray-600 mb-4">
          <strong>Optional.</strong> Knowledge Base and Vendor Discovery features work out-of-the-box using our platform API key.
          Setting your own key gives you <strong>complete control of your data</strong> — all AI requests will use your Anthropic
          account instead of ours. Get your API key from{' '}
          <a
            href="https://console.anthropic.com/settings/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Anthropic Console
          </a>
        </p>

        {claudeKeyExists && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-800 font-medium">API key configured</span>
              <span className="text-xs text-green-600 font-mono">sk-ant-••••••••</span>
            </div>
            <button
              onClick={() => deleteSecret('claudeApiKey')}
              disabled={saving}
              className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        )}

        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); saveSecret('claudeApiKey', claudeApiKey, claudeKeyShared); }}>
          <label className="block text-sm font-medium text-gray-700">
            {claudeKeyExists ? 'Update API Key' : 'Set API Key'}
          </label>
          <div className="relative">
            <input
              type={showClaudeKey ? 'text' : 'password'}
              value={claudeApiKey}
              onChange={(e) => setClaudeApiKey(e.target.value)}
              placeholder="sk-ant-api03-..."
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              disabled={saving}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setShowClaudeKey(!showClaudeKey)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showClaudeKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div className="flex items-center">
            <input
              id="claude-shared"
              type="checkbox"
              checked={claudeKeyShared}
              onChange={(e) => setClaudeKeyShared(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={saving}
            />
            <label htmlFor="claude-shared" className="ml-2 block text-sm text-gray-700">
              Share with team (all team members can use this API key)
            </label>
          </div>
          <button
            type="submit"
            disabled={saving || !claudeApiKey.trim() || !claudeApiKey.startsWith('sk-ant-')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {saving ? 'Saving...' : claudeKeyExists ? 'Update Key' : 'Save Key'}
          </button>
          {claudeApiKey && !claudeApiKey.startsWith('sk-ant-') && (
            <p className="text-xs text-red-600">Claude API keys should start with "sk-ant-"</p>
          )}
          {!claudeKeyShared && (
            <p className="text-xs text-amber-600">⚠️ Only you will use this API key. Other team members will use the platform's default API key instead.</p>
          )}
        </form>
      </div>

      {/* OpenAI API Key */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-md font-semibold text-gray-900 mb-2">OpenAI API Key</h3>
        <p className="text-sm text-gray-600 mb-4">
          <strong>Optional.</strong> Future OpenAI integrations will work out-of-the-box using our platform API key.
          Setting your own key gives you <strong>complete control of your data</strong> — all AI requests will use your OpenAI
          account instead of ours. Get your API key from{' '}
          <a
            href="https://platform.openai.com/api-keys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            OpenAI Platform
          </a>
        </p>

        {openaiKeyExists && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-800 font-medium">API key configured</span>
              <span className="text-xs text-green-600 font-mono">sk-••••••••</span>
            </div>
            <button
              onClick={() => deleteSecret('openaiApiKey')}
              disabled={saving}
              className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        )}

        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); saveSecret('openaiApiKey', openaiApiKey, openaiKeyShared); }}>
          <label className="block text-sm font-medium text-gray-700">
            {openaiKeyExists ? 'Update API Key' : 'Set API Key'}
          </label>
          <div className="relative">
            <input
              type={showOpenaiKey ? 'text' : 'password'}
              value={openaiApiKey}
              onChange={(e) => setOpenaiApiKey(e.target.value)}
              placeholder="sk-..."
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              disabled={saving}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setShowOpenaiKey(!showOpenaiKey)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showOpenaiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div className="flex items-center">
            <input
              id="openai-shared"
              type="checkbox"
              checked={openaiKeyShared}
              onChange={(e) => setOpenaiKeyShared(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={saving}
            />
            <label htmlFor="openai-shared" className="ml-2 block text-sm text-gray-700">
              Share with team (all team members can use this API key)
            </label>
          </div>
          <button
            type="submit"
            disabled={saving || !openaiApiKey.trim() || !openaiApiKey.startsWith('sk-')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {saving ? 'Saving...' : openaiKeyExists ? 'Update Key' : 'Save Key'}
          </button>
          {openaiApiKey && !openaiApiKey.startsWith('sk-') && (
            <p className="text-xs text-red-600">OpenAI API keys should start with "sk-"</p>
          )}
          {!openaiKeyShared && (
            <p className="text-xs text-amber-600">⚠️ Only you will use this API key. Other team members will use the platform's default API key instead.</p>
          )}
        </form>
      </div>
    </div>
  )
}
